import type { BeatPartLoop, BeatPartMoveState, BeatPartWorld } from '@/game/beatPartModule';
import type { HedgerowBoardsSeam } from '@/game/roomModule';
import { disposeAsset } from '@/models/materials';
import { buildFenceBoards } from '@/models/env/fence-boards';
import type { BloomCensus } from '@/render/bloom';
import { getZone, wearForBeat } from '@/render/palette';
import { OUT_CUES, OUT_FLAGS, OUT_IDS } from '@/story/act1/out';
import {
  BOARDS_AT, BOARDS_BREAK_SPEED, DITCH_AT, HEDGEROW_BLOOM_ZONE_ID, HEDGEROW_FOG_SCALE, HEDGEROW_ZONE_ID, HEDGE_GAP_AT,
  PAST_FIELD_FENCE_Z, hedgerowGroundY, isUnderTrailerHedgerow,
} from '@/world/hedgerow';
import { hedgerowCover } from '@/world/hedgerowStealth';
import { OutDirector, type Framing } from '@/world/outDirector';
import type { Interactable } from '@/systems/interaction';

type Pose = { x: number; y: number; z: number; facing: number };
type Vec = { readonly x: number; readonly y: number; readonly z: number };
type OutCue = Parameters<OutDirector['handle']>[0];

/** The `outDebug` shape the specs read. */
export type OutDebug = OutDirector['debug'] & {
  bloomCensus: BloomCensus; fade: number; sheltered: boolean; cameraHeld: boolean; boardAnswers: number;
};

/** Shared so a tick with no placement allocates nothing. */
const NO_PLACEMENTS: readonly Pose[] = [];

/**
 * Beat 7, *Out* — the field's step, its presentation and its loops.
 *
 * It was inline in `main.ts` across nine call sites until session 78, then this
 * class called from those sites, and now it is driven by beat 7's part module
 * (`src/game/beats/out.ts`), which says which contract hook carries each of the
 * orderings that matter: `tryBreakBoards` before the controller
 * (`beforeMove`), then `step`, the `placements()` applied through `moveSelf`,
 * and only then `addStoryFlags` — which reads where she ended up. Everything it
 * touches outside itself arrives through `BeatPartWorld`, including the room's
 * bay (`HedgerowBoardsSeam`).
 *
 * There is deliberately no light grid, no cover volumes for a watcher and no
 * `StealthSystem` here. Every other staged room in the game has all three, so the
 * absence is worth a note rather than a shrug: **beat 7 has no watcher and no
 * fixture.** Nobody looks for her — the dogs work a scent trail, which is a
 * different system with a different fairness contract — and a room with zero
 * point lights has nothing for a light grid to vary over. Adding a stealth system
 * here to match the other rooms would bake a uniform grid, evaluate an empty
 * watcher list every fixed step, and produce exactly the confident-looking zero
 * that D-100 exists to distrust. The cover list still matters, and `OutDirector`
 * holds it: it is doing double duty as the rain-shelter list (D-118), which is
 * the beat's whole mechanic.
 */
export class HedgerowOut {
  readonly director: OutDirector;
  readonly interactables: readonly Interactable[];
  readonly #world: BeatPartWorld;
  readonly #seam: HedgerowBoardsSeam;
  /**
   * The story's flag set, as the entry point that is running was handed it.
   * The boards answer from inside `tryBreakBoards` and `step` (a slow roll's
   * strain) as well as from a verb, and each of those calls brings its own.
   */
  #raiseFlag: (flag: string) => void = () => {
    throw new Error('HedgerowOut: a flag was raised outside a step');
  };
  #sheltered = false;
  #rain: BeatPartLoop | null = null;
  #fieldAir: BeatPartLoop | null = null;
  #placedPastFence = false;
  #placedInWater = false;
  /** Seconds since the timber last shuddered under a failed verb, or null. */
  #shudderAge: number | null = null;
  #shudderStrength = 0;
  #boardAnswers = 0;

  constructor(world: BeatPartWorld) {
    const seam = world.room.hedgerowBoards;
    if (seam === undefined) throw new Error('HedgerowOut: the room offers no boards seam — beat 7 booted outside the hedgerow');
    this.#world = world;
    this.#seam = seam;
    const zone = getZone(HEDGEROW_ZONE_ID);
    const bloomZone = getZone(HEDGEROW_BLOOM_ZONE_ID);
    if (!zone) throw new Error(`unknown zone "${HEDGEROW_ZONE_ID}"`);
    if (!bloomZone) throw new Error(`unknown zone "${HEDGEROW_BLOOM_ZONE_ID}"`);
    const { scene } = world;
    const audio = { playOneShot: world.playOneShot };

    /**
     * The bloom, and it is constructed here because it needs the post stack.
     *
     * `exposureFrom` is **2.60 against the room's authored 1.52**, and that is the
     * value lever the whole beat leans on. The lights do not change during a bloom
     * — their colour comes from the zone uniforms and their intensity is authored
     * once — so what makes the pre-bloom frame *dim* is that everything is measured
     * against a high reference. At 2.60 the room's irradiance of 1.52 lands at
     * 0.565 on the ramp, which is between `TOON_RAMP.thresholds` 0.32 and 0.62:
     * **the mid band.** Dim, and fully legible.
     *
     * That second half matters as much as the first. Session 26 shipped a yard too
     * dark to play in and the lesson was that *a route you cannot see is not a
     * route you can plan* — and stage 3 asks her to roll eighteen metres down this
     * one. If a stopwatch says the fall is too dark, **this number is the lever**,
     * not the key and not the ambient.
     */
    const bloom = world.createBloom(zone, bloomZone, {
      // **The same constant the room's own lighting uses.** `BloomOptions.fogScale`
      // defaults to 1, and omitting it here overwrote the room's authored haze on
      // the first render frame of the beat — see `HEDGEROW_FOG_SCALE`.
      fogScale: HEDGEROW_FOG_SCALE,
      exposureFrom: 2.6,
      exposureTo: 1.52,
      skyFrom: 0.22,
      skyTo: 1,
    });

    this.director = new OutDirector(scene, {
      bloom,
      cover: hedgerowCover(),
      groundY: hedgerowGroundY,
      // The shed door. The dogs start at his feet and the men's trail runs along
      // the wall beside him — which is the ground stage 1 is about.
      handlerAt: { x: -1, z: 10.6 },
      bankZ: DITCH_AT.nearBankZ,
      boardsAt: BOARDS_AT,
      boardsBreakSpeed: BOARDS_BREAK_SPEED,
      underTrailerAt: { x: 3, y: hedgerowGroundY(3, 8), z: 8, facing: Math.PI },
      // Just through the bay, on the field side, already pointing downhill.
      pastFenceAt: { x: BOARDS_AT.x, y: hedgerowGroundY(BOARDS_AT.x, -0.9), z: -0.9, facing: Math.PI },
      // On the bed, in the water, facing east — which is −X, and which is the way
      // she wades and the way the dawn is. See `docs/hedgerow-layout.md`.
      inWaterAt: { x: BOARDS_AT.x, y: DITCH_AT.bedY, z: -19.2, facing: -Math.PI / 2 },
      fieldMark: HEDGE_GAP_AT,
      // From the arc, never from a literal here. A second statement of the ramp is
      // how `setWear()` came to have zero callers for six beats (D-116).
      wearFrom: wearForBeat(6),
      wearTo: wearForBeat(7),
      onBoardsGo: () => {
        // **The mesh swap and the collider removal, in one place.** Rule 2's whole
        // mechanism, and the only line in the beat where the thing you can see and
        // the thing that stops her could be made to disagree.
        const broken = buildFenceBoards('broken');
        // `boards` is the outer instancing group, whose own transform is
        // intentionally identity; the actual intact placement is baked into its
        // instance matrix. Copying that outer transform put the broken panel at
        // the world origin. This is a one-off replacement, so place it from the
        // room's single authored opening instead of trying to recover an instance
        // transform after its source has been consumed.
        broken.position.set(BOARDS_AT.x, hedgerowGroundY(BOARDS_AT.x, BOARDS_AT.z), BOARDS_AT.z);
        broken.rotation.y = BOARDS_AT.rotY;
        seam.boards.removeFromParent();
        disposeAsset(seam.boards);
        scene.add(broken);
        seam.removeBoardsCollider();
        // Nothing left to push: a prompt on the gap would be a prompt on air.
        world.interaction.setEnabled(OUT_IDS.boards, false);
      },
      // One hit, and it is the loudest thing in Act 1. It happens once per game.
      onBoardsBreak: (at) => audio.playOneShot('boards-break', { gain: 1, position: at }),
      onBoardsStrain: (strength) => this.#answerBoards(strength),
      onWater: (at) => audio.playOneShot('ditch-water', { gain: 0.8, position: at }),
      // **The same whistle that called the dog OFF in beat 4**, and that Kestner
      // used in beat 6 to call somebody on. The player already knows what it means.
      onWhistle: (at) => audio.playOneShot('handler-whistle', { gain: 0.9, position: at }),
      onSnuff: (at) => audio.playOneShot('dog-snuff', { gain: 0.6, position: at }),
      onClaws: (at) => audio.playOneShot('dog-claws-concrete', { gain: 0.5, position: at }),
      onSheltered: (sheltered) => {
        this.#sheltered = sheltered;
      },
    });

    this.interactables = [
      // ── Two verbs on the timber, and BOTH OF THEM FAIL ───────────────────
      //
      // That is not a bug and it is not an oversight; it is how the beat teaches
      // without words. D-043 says a verb the beat has not offered must not be
      // reachable, and the corollary is that a verb it HAS offered must be allowed
      // to fail honestly. She cannot push the boards and she cannot root them
      // open. What breaks them is arriving fast, and the only way to be fast is to
      // roll — spec §5.4's *"smashes through rotten boards"*, taught by the fence
      // simply not opening.
      //
      // So these two verbs never complete the stage. **Do not "fix" that by
      // wiring them to `onBoardsGo`**, and do not add a prompt: if a stopwatch
      // says the speed threshold is unreachable, the lever is
      // `BOARDS_BREAK_SPEED`.
      //
      // **Failing honestly is not failing silently** (D-209, audit row 8): each
      // try makes the timber shudder and creak and hold, and raises
      // `boardsTried`, which turns her wayfinding look back up the run-up.
      //
      // **One target with both verbs, not two targets.** They were two, at the
      // same X/Z and reach, and candidate selection ignores height and keeps
      // the first on a tie — so the root could never be chosen (Codex review,
      // session 71). `once: false` so every hold answers, not only the first.
      {
        id: OUT_IDS.boards,
        at: { x: BOARDS_AT.x, y: 0.45, z: BOARDS_AT.z },
        verbs: ['nudge', 'root'],
        reach: 1.2,
        duration: 1.8,
        once: false,
        enabled: false,
      },
    ];
  }

  /**
   * The boards have to break before this tick's controller movement resolves.
   * Once Rapier has met the intact collider it correctly reports no movement
   * and therefore no speed, which is too late to distinguish a roll from a
   * walk. `self` is the previous fixed step's simulation state, so this is the
   * real arriving roll, not an input-only guess or a scripted placement.
   */
  tryBreakBoards(self: BeatPartMoveState, raiseFlag: (flag: string) => void): void {
    this.#raiseFlag = raiseFlag;
    this.director.tryBreakBoards({
      x: self.x,
      z: self.z,
      facing: self.facing,
      speed: self.speed,
      rolling: self.gait === 'roll',
    });
  }

  step(dt: number, self: Vec, raiseFlag: (flag: string) => void): void {
    this.#raiseFlag = raiseFlag;
    this.director.step(dt, { x: self.x, y: self.y, z: self.z });
  }

  /**
   * The moves the director asked for this tick, in the order the host must apply
   * them. Each one is `character.reset` — the controller is the only thing
   * allowed to move a body, so this class names the pose and the host moves her.
   *
   * The two forward moves latch here for the same reason beat 6's do: a getter
   * that stays true forever would re-reset her every tick and pin her to the
   * spot. `boardsGo` and `driveHerIn` are both backstops, so both of them are
   * moves the player did not make.
   *
   * **Capture REPEATS.** The other two are once-per-beat and latch forever.
   * Capture can happen twice, so the director clears its own edge once the body
   * has been handed back — a latch here would make the second capture silently
   * do nothing, which is the same defect the director's first build had in the
   * other direction (it cleared a flag and moved nothing at all).
   */
  placements(): readonly Pose[] {
    const director = this.director;
    if (!(director.placePastFence && !this.#placedPastFence)
      && !(director.placeInWater && !this.#placedInWater)
      && !director.placeUnderTrailer) return NO_PLACEMENTS;
    const poses: Pose[] = [];
    if (director.placePastFence && !this.#placedPastFence) {
      this.#placedPastFence = true;
      poses.push(director.pastFenceAt);
    }
    if (director.placeInWater && !this.#placedInWater) {
      this.#placedInWater = true;
      poses.push(director.inWaterAt);
    }
    if (director.placeUnderTrailer) {
      poses.push(director.underTrailerAt);
      director.clearPlaceUnderTrailer();
      // Re-arm the forward placements the director just reset. Without this a
      // player captured after `boardsGo` had already fired could never be put
      // through the fence again.
      this.#placedPastFence = false;
      this.#placedInWater = false;
    }
    return poses;
  }

  /**
   * The flags the ROOM raises, all four of them geometric, read AFTER the
   * placements so they see where she was put.
   *
   * Every one is a fact about where she is, which is why they are here and not
   * in `out.ts` (which may not know what a room is) and not in the director
   * (which does not get to decide when a stage ends).
   */
  addStoryFlags(flags: { add(flag: string): unknown }, self: Vec): void {
    if (!isUnderTrailerHedgerow(self.x, self.z)) flags.add(OUT_FLAGS.leftTheTrailer);
    // **Past the fence LINE, not past the boards' collider.** She can be
    // through the bay and still touching it, and the stage that ends here is
    // the one whose backstop is capture — so it has to end the moment she is
    // on the field side, or a player who broke through and stopped in the
    // gateway gets taken by the dogs for having succeeded.
    if (self.z < PAST_FIELD_FENCE_Z) flags.add(OUT_FLAGS.pastTheFence);
    if (
      self.z <= DITCH_AT.channelNearZ && self.z >= DITCH_AT.channelFarZ
      && self.x >= DITCH_AT.minX && self.x <= DITCH_AT.maxX
    ) flags.add(OUT_FLAGS.inTheWater);
    if (self.z < DITCH_AT.farBankZ - 1.4) flags.add(OUT_FLAGS.throughTheHedge);
    // The one flag the DIRECTOR owns, because "she has decided she is done
    // with the field" is a pair of thresholds and rule 4's whole claim.
    if (this.director.headingOut) flags.add(OUT_FLAGS.headingOut);
  }

  /** An interaction event from the host. Only the timber's two verbs mean anything here. */
  answerVerb(kind: string, id: string, raiseFlag: (flag: string) => void): void {
    this.#raiseFlag = raiseFlag;
    if ((kind === 'nudge' || kind === 'root') && id === OUT_IDS.boards) {
      // The timber gives and holds: a shudder on the render step and a
      // dull knock, the break's own crack pitched down and quietened so it
      // is plainly the same wood and plainly not breaking.
      this.#answerBoards(0);
    }
  }

  handleCue(cue: { id: string }): void {
    this.director.handle(cue as OutCue);
    if (cue.id === OUT_CUES.end) {
      // ── Stop the loops ─────────────────────────────────────────────────────
      //
      // `renderAudio` already stops *updating* them once the director has ended,
      // which is not the same as stopping them: an `AudioHandle` left running
      // keeps running. Beat 5 shipped a plant that went on thumping behind the
      // black screen at the end of Act 1 indefinitely (D-087/D-090), and this is
      // the end of Act 1 again. Found on independent review, which noticed that
      // `OUT_CUES.end` had no handler at all while `YARD_CUES.end` does.
      this.#stopLoops(2.5);
    }
    if (cue.id === OUT_CUES.offerBoards) {
      // The director's `offerBoards` case has said since beat 7 was built that
      // the host enables the two verbs, and nothing did: they were declared
      // disabled and stayed so, and the fence offered no prompt at all. Found
      // walking the (c) route from spawn (D-209); the audit's row 8 read the
      // comment, not the game.
      this.#world.interaction.setEnabled(OUT_IDS.boards, true);
    }
  }

  /** True while the beat owns the frame and the verb prompt must hide. */
  get promptSuppressed(): boolean {
    return this.director.cameraHeld || this.director.fade > 0.5;
  }

  framing(): Framing | null {
    return this.director.framing();
  }

  /**
   * The timber's shudder, then the dogs' legs, the fade, the wear ramp and THE
   * BLOOM. All of it is something the player sees and nothing the simulation
   * may branch on.
   */
  renderWorld(frameDt: number): void {
    if (this.#shudderAge !== null) {
      // The timber answering a failed push (D-209). Along the fence normal only
      // — the boards group's own transform is identity (its placement is baked
      // into the instance matrix, see `onBoardsGo`), so a translation is safe
      // where a rotation would swing about the origin. A broken bay's group is
      // off the scene, and moving it is harmless.
      this.#shudderAge += frameDt;
      const settled = this.#shudderAge > 0.7;
      this.#seam.boards.position.z = settled
        ? 0
        : -(0.035 + 0.06 * this.#shudderStrength) * Math.exp(-6 * this.#shudderAge) * Math.sin(this.#shudderAge * Math.PI * 2 * 11);
      if (settled) this.#shudderAge = null;
    }
    this.director.update(frameDt);
  }

  /**
   * The two loops, acquired HERE and not at construction.
   *
   * **`!active` and not just `null`.** `AudioEngine.play` hands back a
   * permanently inert `SilentHandle` until the audio context is running, and
   * the context only unlocks on the player's first gesture — long after
   * `boot()`. A `??=` would latch that silent handle for the whole beat. Four
   * beats have now paid for this, most recently all three of beat 6's loops,
   * which were silent in every normal play and were found on review rather
   * than by anything that could hear.
   */
  renderAudio(): void {
    if (this.director.ended) return;
    const audio = this.#world;
    if (this.#rain === null || !this.#rain.active) {
      this.#rain = audio.playLoop('rain-bed', { gain: 0 });
    }
    // Under the trailer the storm is quieter, the same way it is in the yard.
    this.#rain.setGain(this.#sheltered ? 0.2 : 0.42, 0.35);

    // `field-air` comes up ON THE CUT and never goes down again, and it is
    // gated on the bloom having started rather than on a stage id — one number,
    // one condition, and the sound and the picture cannot get out of step.
    //
    // It is the **first quiet in this game that is not tension** (spec §8 puts
    // music outdoors and beat 7 is where it belongs). The strings that should
    // arrive with it are a declared cut line and are filed in `docs/backlog.md`;
    // `field-air` alone is honest, and a rushed string bed under the game's best
    // shot would not be.
    if (this.director.debug.bloom > 0) {
      if (this.#fieldAir === null || !this.#fieldAir.active) {
        this.#fieldAir = audio.playLoop('field-air', { gain: 0 });
      }
      // Slower than the bloom's seven seconds on purpose: the air arrives
      // under the colour rather than with it.
      this.#fieldAir.setGain(0.5, 9);
      // And the weather goes out as the light comes up. This is the ONE place
      // the rain moves during the beat, and it moves because the shot needs
      // somewhere quiet to land — not because the storm is a timer.
      this.#rain.setGain(0.08, 9);
    }
  }

  /** The `outDebug` shape the specs read. */
  get debug(): OutDebug {
    const director = this.director;
    return {
      ...director.debug,
      bloomCensus: director.bloomCensus,
      fade: director.fade,
      sheltered: this.#sheltered,
      // Exported because `framing()` silently beats `shot()` on every render
      // frame, and three cameras were lost to that in one session. Any spec that
      // wants its own camera in this room waits on this being false.
      cameraHeld: director.cameraHeld,
      boardAnswers: this.#boardAnswers,
    };
  }

  dispose(): void {
    // The host used to dispose only the director and leave the two loops to the
    // `end` cue; a context loss before `end` left them playing. Stopped here
    // too, so everything this class starts, this class stops.
    this.#stopLoops(0);
    this.director.dispose();
  }

  /**
   * The timber taking a push or a slow roll and holding (D-209). The answer
   * scales with how close she came to `BOARDS_BREAK_SPEED` — a nudge is 0, a
   * roll that nearly made it nearly 1 — so the fence itself says "more". The
   * break's own crack, pitched down and quietened: plainly the same wood,
   * plainly not breaking.
   */
  #answerBoards(strength: number): void {
    this.#raiseFlag(OUT_FLAGS.boardsTried);
    this.#boardAnswers += 1;
    this.#shudderAge = 0;
    this.#shudderStrength = strength;
    this.#world.playOneShot('boards-break', {
      gain: 0.3 + 0.35 * strength,
      position: { x: BOARDS_AT.x, y: hedgerowGroundY(BOARDS_AT.x, BOARDS_AT.z) + 0.5, z: BOARDS_AT.z },
      detune: -900 + 500 * strength,
    });
  }

  #stopLoops(fade: number): void {
    this.#rain?.stop(fade);
    this.#rain = null;
    this.#fieldAir?.stop(fade);
    this.#fieldAir = null;
  }
}
